import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_BASE } from '../config';
import { useTranslation } from '../translations';
import {
  PanelContainer, PageHeader, TitleRow, Title, Subtitle, HeaderActions, PanelCard,
  PrimaryButton, IconButton, FieldLabel, FieldHint, TextInput, LinkButton,
} from '../ui/common';
import { ArrowLeftIcon, PrinterIcon, SearchIcon } from '../ui/icons';
import { Banner } from '../components/ui/Banner';
import SelectDropdown from '../components/ui/SelectDropdown';
import { LABEL_LAYOUTS, DEFAULT_LAYOUT, MAX_COPIES, perPage, slotPosition, paginateLabels } from '../utils/labelLayouts';
import { labelCodeFor, barcodeValueFor, isCode128Safe, systemCodeFor } from '../utils/equipmentCode';
import { groupItems, groupState, locateItems, slotStates, shortList } from '../utils/labelSelection';
import { qrDataUrl, code128DataUrl } from '../components/labels/codeImages';
import PrintFrame, { printFrame } from '../components/print/PrintFrame';
import LabelLogoPicker from '../components/labels/LabelLogoPicker';
import { effectiveLabelLogo } from '../utils/labelLogo';

// Etykiety z kodem QR i/lub kreskowym do naklejenia na sprzęt (2026-09-24, przebudowa 2026-09-29).
//
// Co trafia na naklejkę, rozstrzyga `utils/equipmentCode.js#labelCodeFor` — ta sama reguła,
// którą rozpoznają skanery w panelu. Drukuje przeglądarka (`window.print`): działa z każdą
// drukarką, także etykiet, bez sterowników po stronie serwera i bez PDF-a.
//
// UKŁAD „STÓŁ DO DRUKU” (2026-09-29). Strona musi działać tak samo dla jednej sztuki, jak dla
// całego magazynu. Przy kilkuset sztukach płaska lista „pigułek” robiła się ścianą bez
// struktury (a jedyną akcją było „×”, więc usuniętej sztuki nie dało się przywrócić), a podgląd
// wszystkich arkuszy w pełnym rozmiarze oznaczał kilkanaście kartek A4 do przewinięcia. Dlatego:
// lista po lewej jest pogrupowana po kategorii, z wyszukiwarką i polami wyboru, a podgląd
// pokazuje JEDEN arkusz, przeskalowany do kolumny, z paskiem miniatur wszystkich arkuszy.
//
// DRUK IDZIE Z UKRYTEJ RAMKI, NIE ZE STRONY APLIKACJI (2026-09-29). Ramka ma własny, czysty
// dokument: tylko WSZYSTKIE arkusze (podgląd na ekranie rysuje jeden) i reguła `@page`, bez
// menu, bez `#root` i bez stylów panelu. Wcześniej drukowała się cała strona, a arkusze leżały
// w portalu na `<body>` odkrywanym regułą CSS na czas druku — działało, ale przeglądarka
// budowała podgląd z całej aplikacji, a okno druku w Edge rysowało się przesunięte i nie
// zasłaniało strony. Sama ramka to wspólny components/print/PrintFrame.js (od U.29 drukuje nią
// też plan dnia w kalendarzu).
// Ctrl+P na tej stronie też drukuje ramkę; druk z menu przeglądarki drukuje zwykłą stronę.
//
// LOGO ZAMIAST NAZWY (24.10.2026). Nazwa sprzętu zajmowała połowę naklejki, a i tak bywała
// ucięta; przy ladzie sztukę rozpoznaje się skanem. W jej miejscu stoi logo firmy (osobne logo
// etykiet albo to z „Danych firmy”, utils/labelLogo.js), a QR jest nieco mniejszy, żeby logo
// zmieściło się obok. QR o boku 14 mm (najmniejszy arkusz) skanuje się telefonem bez problemu.

const FORMATS = ['qr', 'barcode', 'both'];
const STORAGE_KEY = 'equipmentLabels.options.v1';
// Chrome liczy `mm` przy 96 dpi — ta sama stała co w components/document/PaperSheet.js.
const MM_TO_PX = 96 / 25.4;
// Przy tylu sztukach lista startuje zwinięta do kategorii; mniej mieści się bez przewijania.
const EXPAND_ALL_UP_TO = 30;

function loadOptions() {
  try {
    const o = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '{}');
    return {
      layout: LABEL_LAYOUTS[o.layout] ? o.layout : DEFAULT_LAYOUT,
      format: FORMATS.includes(o.format) ? o.format : 'both',
      showLogo: o.showLogo !== false,
    };
  } catch (_) {
    return { layout: DEFAULT_LAYOUT, format: 'both', showLogo: true };
  }
}

// ── Etykieta i arkusz (wymiary w mm, jak na opakowaniu arkusza) ─────────────────────────────

const Sheet = styled.div`
  position: relative;
  width: ${(p) => p.$w}mm;
  height: ${(p) => p.$h}mm;
  background: #fff;
  color: #000;
  overflow: hidden;
  box-sizing: border-box;

  @media print {
    page-break-after: always;
    break-after: page;
    &:last-child { page-break-after: auto; break-after: auto; }
  }
`;

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(37, 99, 235, 0); }
  40% { box-shadow: 0 0 0 3mm rgba(37, 99, 235, 0.28); }
`;

const Slot = styled.div`
  position: absolute;
  box-sizing: border-box;
  padding: ${(p) => p.$pad}mm;
  display: flex;
  gap: ${(p) => p.$pad}mm;
  align-items: center;
  overflow: hidden;
  font-family: Arial, Helvetica, sans-serif;

  @media screen {
    outline: 1px dashed #d4d4d8;
    outline-offset: -1px;
    ${(p) => p.$highlight && css`
      outline: 0.8mm solid var(--primary);
      z-index: 1;
      animation: ${pulse} 1.2s ease-out 2;
      @media (prefers-reduced-motion: reduce) { animation: none; }
    `}
  }
`;

const TextCol = styled.div`
  flex: 1;
  min-width: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.6mm;
`;

// Logo mieści się w przydzielonym pasku (wysokość podaje LabelBody), zachowując proporcje.
const LabelLogo = styled.img`
  display: block;
  width: auto;
  height: auto;
  max-width: 100%;
  min-height: 0;
  object-fit: contain;
  flex-shrink: 1;
`;

const LabelCode = styled.div`
  font-family: 'Courier New', monospace;
  font-weight: 700;
  line-height: 1.1;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

function LabelBody({ item, layout, format, logo, images }) {
  const { w, h } = layout.label;
  const pad = Math.max(1, Math.min(2.5, h * 0.07));
  const inner = h - 2 * pad;
  const code = labelCodeFor(item);
  const barValue = barcodeValueFor(item);
  const codeFs = Math.max(1.8, Math.min(3.2, h * 0.1));
  const qr = images.qr.get(code);
  const bar = images.bar.get(barValue);

  if (format === 'barcode') {
    return (
      <TextCol style={{ alignItems: 'center' }}>
        {logo && <LabelLogo src={logo} alt="" data-label-logo="true" style={{ maxHeight: `${inner * 0.28}mm` }} />}
        {bar && <img src={bar} alt="" style={{ width: '100%', height: `${inner * (logo ? 0.44 : 0.62)}mm`, objectFit: 'fill' }} />}
        <LabelCode style={{ fontSize: `${codeFs}mm`, textAlign: 'center', maxWidth: '100%' }}>{barValue}</LabelCode>
      </TextCol>
    );
  }

  // QR do 40% szerokości (dawniej 48%) i do 86% wysokości: obok musi zmieścić się logo.
  const qrSize = Math.min(inner * 0.86, (w - 2 * pad) * 0.4);
  const logoH = inner * (format === 'both' ? 0.36 : 0.56);
  return (
    <>
      {qr && <img src={qr} alt="" style={{ width: `${qrSize}mm`, height: `${qrSize}mm`, flexShrink: 0 }} />}
      <TextCol style={{ alignItems: 'center' }}>
        {logo && <LabelLogo src={logo} alt="" data-label-logo="true" style={{ maxHeight: `${logoH}mm` }} />}
        {format === 'both' && bar && (
          <img src={bar} alt="" style={{ width: '100%', height: `${inner * 0.28}mm`, objectFit: 'fill' }} />
        )}
        <LabelCode style={{ fontSize: `${codeFs}mm`, textAlign: 'center', maxWidth: '100%' }}>{code}</LabelCode>
      </TextCol>
    </>
  );
}

// `firstIndex` = numer pierwszej strony w `pages`, żeby podgląd jednej strony znał swój numer.
function Sheets({ pages, firstIndex = 0, layout, format, logo, images, dataCy, highlight }) {
  const pad = Math.max(1, Math.min(2.5, layout.label.h * 0.07));
  // Klucze to pozycje na arkuszu: ta sama sztuka może stać w kilku polach (kopie).
  return pages.map((page, i) => {
    const pi = firstIndex + i;
    return (
      <Sheet key={pi} $w={layout.page.w} $h={layout.page.h} data-cy={dataCy ? `${dataCy}-sheet` : undefined}>
        {page.map((item, si) => {
          const pos = slotPosition(layout, si);
          const lit = highlight && highlight.page === pi && highlight.slot === si;
          return (
            <Slot
              key={lit ? `${si}-${highlight.stamp}` : si}
              $pad={pad}
              $highlight={lit}
              style={{ left: `${pos.left}mm`, top: `${pos.top}mm`, width: `${layout.label.w}mm`, height: `${layout.label.h}mm` }}
              data-cy={item && dataCy ? `${dataCy}-label` : undefined}
              data-code={item ? labelCodeFor(item) : undefined}
              data-highlight={lit ? 'true' : undefined}
            >
              {item && <LabelBody item={item} layout={layout} format={format} logo={logo} images={images} />}
            </Slot>
          );
        })}
      </Sheet>
    );
  });
}

// ── Układ strony ────────────────────────────────────────────────────────────────────────────

// Pierwsza kolumna (arkusz / drukarka) szersza: nazwy układów są długie, a ucięta nazwa
// w przycisku listy nie mówi, który arkusz jest wybrany.
const Options = styled.div`
  display: grid;
  grid-template-columns: minmax(260px, 1.3fr) repeat(auto-fit, minmax(180px, 1fr));
  gap: 14px 18px;
  align-items: start;

  @media (max-width: 640px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Radios = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;

  label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 12px;
    border-radius: var(--r-pill);
    border: 1px solid var(--input-border);
    font-size: var(--fs-sm);
    cursor: pointer;
  }
  label[data-on='true'] { border-color: var(--primary); background: var(--info-bg); font-weight: 600; }
  input { accent-color: var(--primary); }
`;

const Workbench = styled.div`
  display: grid;
  grid-template-columns: minmax(280px, 360px) minmax(0, 1fr);
  gap: 16px;
  align-items: start;

  @media (max-width: 960px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

// Kolumna wyboru przykleja się do góry i przewija w środku, żeby przy długim podglądzie
// lista i arkusz były widoczne naraz. Na wąskim ekranie przestaje być przyklejona.
const Picker = styled(PanelCard)`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  position: sticky;
  top: 16px;
  max-height: calc(100vh - 48px);
  min-height: 0;

  @media (max-width: 960px) {
    position: static;
    max-height: none;
  }
`;

const PickerHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;

  h2 { margin: 0; font-size: var(--fs-md); font-weight: 700; }
  span { font-size: var(--fs-xs); color: var(--text-muted); font-variant-numeric: tabular-nums; }
`;

const Search = styled.div`
  position: relative;
  svg { position: absolute; left: 11px; top: 50%; transform: translateY(-50%); color: var(--text-muted); pointer-events: none; }
  input { padding-left: 34px; width: 100%; box-sizing: border-box; }
`;

const BulkRow = styled.div`
  display: flex;
  gap: 14px;
  font-size: var(--fs-sm);
`;

const GroupList = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  margin: 0 -6px;
  padding: 0 6px;
  overscroll-behavior: contain;

  @media (max-width: 960px) {
    max-height: 60vh;
  }
`;

const GroupHead = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 4px;
  background: var(--card-bg);
  border-bottom: 1px solid var(--input-border);

  input { accent-color: var(--primary); width: 16px; height: 16px; flex-shrink: 0; }
`;

const GroupToggle = styled.button`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  border: 0;
  background: none;
  padding: 2px 0;
  color: var(--text);
  font: inherit;
  font-size: var(--fs-sm);
  font-weight: 700;
  text-align: left;
  cursor: pointer;

  svg { flex-shrink: 0; transition: transform 0.15s ease; transform: rotate(${(p) => (p.$open ? 270 : 180)}deg); color: var(--text-muted); }
  .name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .count { margin-left: auto; font-weight: 500; font-size: var(--fs-xs); color: var(--text-muted); font-variant-numeric: tabular-nums; }
  &:disabled { cursor: default; }
  &:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; border-radius: var(--r-sm); }
  @media (prefers-reduced-motion: reduce) { svg { transition: none; } }
`;

const Rows = styled.ul`
  list-style: none;
  margin: 2px 0 8px;
  padding: 0;
`;

const Row = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 3px 4px 3px 26px;
  border-radius: var(--r-sm);
  font-size: var(--fs-sm);
  opacity: ${(p) => (p.$on ? 1 : 0.55)};

  &:hover { background: var(--hover-bg); }
  input { accent-color: var(--primary); width: 15px; height: 15px; flex-shrink: 0; }
  code {
    flex-shrink: 0;
    font-size: var(--fs-xs);
    color: var(--text-muted);
    font-family: ui-monospace, 'SFMono-Regular', Menlo, Consolas, monospace;
  }
`;

const RowName = styled.button`
  flex: 1;
  min-width: 0;
  border: 0;
  background: none;
  padding: 2px 0;
  color: inherit;
  font: inherit;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: ${(p) => (p.disabled ? 'default' : 'pointer')};

  &:not(:disabled):hover { color: var(--primary); text-decoration: underline; text-underline-offset: 2px; }
  &:focus-visible { outline: 2px solid var(--primary); outline-offset: 1px; border-radius: var(--r-sm); }
`;

const Muted = styled.p`
  margin: 12px 4px;
  font-size: var(--fs-sm);
  color: var(--text-muted);
`;

// ── Podgląd: „mata do cięcia” z jednym arkuszem i paskiem miniatur ─────────────────────────

const Stage = styled(PanelCard)`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
`;

const StageBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const PageNav = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;

  output {
    min-width: 128px;
    text-align: center;
    font-size: var(--fs-sm);
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
`;

const Legend = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  gap: 12px;
  font-size: var(--fs-xs);
  color: var(--text-muted);

  li { display: inline-flex; align-items: center; gap: 5px; }
`;

// Wspólny wygląd pola na miniaturze i w legendzie — jedna definicja, żeby legenda nie kłamała.
const cellLook = css`
  box-sizing: border-box;
  border-radius: 1px;
  ${(p) => p.$state === 'item' && css`background: var(--primary);`}
  ${(p) => p.$state === 'skip' && css`
    background: repeating-linear-gradient(135deg, var(--text-muted) 0 1.5px, transparent 1.5px 3.5px);
    opacity: 0.55;
  `}
  ${(p) => p.$state === 'empty' && css`box-shadow: inset 0 0 0 1px var(--input-border);`}
`;

const Swatch = styled.span`
  width: 12px;
  height: 8px;
  display: inline-block;
  ${cellLook}
`;

const Filmstrip = styled.div`
  position: relative; /* punkt odniesienia dla offsetLeft miniatur */
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 4px 2px 8px;
  scroll-snap-type: x proximity;
`;

const Thumb = styled.button`
  flex-shrink: 0;
  scroll-snap-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  border: 0;
  background: none;
  padding: 0;
  cursor: pointer;
  color: var(--text-muted);
  font: inherit;
  font-size: 11px;
  font-variant-numeric: tabular-nums;

  .paper {
    position: relative;
    width: 44px;
    aspect-ratio: ${(p) => p.$ratio};
    background: var(--card-bg);
    border-radius: 2px;
    box-shadow: 0 0 0 1px var(--input-border), var(--shadow-sm);
    transition: transform 0.12s ease, box-shadow 0.12s ease;
  }
  &[aria-current='true'] { color: var(--primary); font-weight: 700; }
  &[aria-current='true'] .paper { box-shadow: 0 0 0 2px var(--primary), var(--shadow-sm); transform: translateY(-2px); }
  &:hover .paper { transform: translateY(-2px); }
  &:focus-visible { outline: none; }
  &:focus-visible .paper { box-shadow: 0 0 0 2px var(--primary), 0 0 0 5px var(--focus-ring, rgba(37, 99, 235, 0.25)); }
  @media (prefers-reduced-motion: reduce) { .paper { transition: none; } }
`;

const Cell = styled.span`
  position: absolute;
  ${cellLook}
`;

// Tło jak mata do cięcia: drobna siatka z grubszą linią co piąte pole. Daje arkuszowi
// „stół”, na którym leży, i odróżnia podgląd druku od reszty panelu.
const Mat = styled.div`
  --mat-line: color-mix(in srgb, var(--text) 7%, transparent);
  --mat-line-strong: color-mix(in srgb, var(--text) 12%, transparent);
  border-radius: var(--r-md);
  padding: 24px 16px;
  display: flex;
  justify-content: center;
  background-color: var(--hover-bg);
  background-image:
    linear-gradient(var(--mat-line-strong) 1px, transparent 1px),
    linear-gradient(90deg, var(--mat-line-strong) 1px, transparent 1px),
    linear-gradient(var(--mat-line) 1px, transparent 1px),
    linear-gradient(90deg, var(--mat-line) 1px, transparent 1px);
  background-size: 80px 80px, 80px 80px, 16px 16px, 16px 16px;
  background-position: -1px -1px;
  overflow: hidden;
`;

const Viewport = styled.div`
  position: relative;
  flex-shrink: 0;
  box-shadow: var(--shadow-lg);
  outline: 1px solid var(--input-border);
`;

const Scaled = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  transform-origin: top left;
`;

const Range = styled.input`
  width: min(320px, 100%);
  accent-color: var(--primary);
`;

function PageThumb({ page, index, layout, skip, current, onSelect, t }) {
  const n = perPage(layout);
  const states = slotStates(page, { pageIndex: index, perPage: n, skip });
  const { w: pw, h: ph } = layout.page;
  return (
    <Thumb
      type="button"
      $ratio={`${pw} / ${ph}`}
      aria-current={current ? 'true' : 'false'}
      aria-label={t('labels.goToSheet').replace('{n}', String(index + 1))}
      onClick={() => onSelect(index)}
      data-cy="labels-thumb"
    >
      <span className="paper" aria-hidden="true">
        {states.map((s, si) => {
          const pos = slotPosition(layout, si);
          return (
            <Cell
              key={si}
              $state={s}
              style={{
                left: `calc(${(pos.left / pw) * 100}% + 0.5px)`,
                top: `calc(${(pos.top / ph) * 100}% + 0.5px)`,
                width: `calc(${(layout.label.w / pw) * 100}% - 1px)`,
                height: `calc(${(layout.label.h / ph) * 100}% - 1px)`,
              }}
            />
          );
        })}
      </span>
      {index + 1}
    </Thumb>
  );
}

function TriStateBox({ state, onChange, label, ...rest }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.indeterminate = state === 'some'; }, [state]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={state === 'all'}
      onChange={onChange}
      aria-label={label}
      aria-checked={state === 'some' ? 'mixed' : state === 'all'}
      {...rest}
    />
  );
}

export default function EquipmentLabels() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [all, setAll] = useState(null);
  const [error, setError] = useState('');
  const [removed, setRemoved] = useState(() => new Set());
  const [opts, setOpts] = useState(loadOptions);
  const [copies, setCopies] = useState(1);
  const [skip, setSkip] = useState(0);
  const [images, setImages] = useState({ qr: new Map(), bar: new Map() });
  const [query, setQuery] = useState('');
  const [openGroups, setOpenGroups] = useState(null); // null = jeszcze nie rozstrzygnięte
  const [pageIndex, setPageIndex] = useState(0);
  const [highlight, setHighlight] = useState(null);
  // Ustawienia firmy z logo: `label_logo` (osobne logo etykiet) i `company_logo` (Dane firmy).
  const [logoSettings, setLogoSettings] = useState({ loaded: false, label_logo: null, company_logo: null });
  const isAdmin = localStorage.getItem('isAdmin') === '1' || localStorage.getItem('isSuperAdmin') === '1';
  const [matWidth, setMatWidth] = useState(0);
  const matRef = useRef(null);
  const filmRef = useRef(null);
  const printFrameRef = useRef(null);

  const requestedIds = useMemo(() => {
    const raw = params.get('ids');
    if (!raw) return null;
    return new Set(raw.split(',').map(Number).filter((n) => Number.isInteger(n) && n > 0));
  }, [params]);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_BASE}/api/equipment`, { withCredentials: true })
      .then((r) => { if (!cancelled) setAll(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (!cancelled) { setAll([]); setError(t('labels.loadError')); } });
    return () => { cancelled = true; };
  }, [t]);

  useEffect(() => {
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(opts)); } catch (_) { /* tryb prywatny */ }
  }, [opts]);

  // Logo nie blokuje strony: gdy odczyt ustawień się nie uda, etykiety drukują się bez logo.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_BASE}/api/contract-settings`, { withCredentials: true })
      .then((r) => {
        if (cancelled) return;
        const s = r.data?.settings || {};
        setLogoSettings({ loaded: true, label_logo: s.label_logo || null, company_logo: s.company_logo || null });
      })
      .catch(() => { if (!cancelled) setLogoSettings((prev) => ({ ...prev, loaded: true })); });
    return () => { cancelled = true; };
  }, []);
  const logo = useMemo(() => effectiveLabelLogo(logoSettings), [logoSettings]);
  const printedLogo = opts.showLogo ? logo.src : null;

  // Kandydaci = to, o co poprosił adres (albo cały magazyn). Odznaczenie NIE usuwa sztuki
  // z listy — da się ją zaznaczyć z powrotem.
  const candidates = useMemo(
    () => (all || []).filter((e) => !requestedIds || requestedIds.has(Number(e.id))),
    [all, requestedIds],
  );
  const isSelected = useCallback((item) => !removed.has(Number(item.id)), [removed]);

  const allGroups = useMemo(() => groupItems(candidates), [candidates]);
  const visibleGroups = useMemo(() => groupItems(candidates, { query }), [candidates, query]);
  // Kolejność na arkuszu = kolejność na liście: kategorie, a w nich nazwy. Etykiety jednej
  // kategorii leżą więc obok siebie, jak sprzęt na regale.
  const items = useMemo(
    () => allGroups.flatMap((g) => g.items).filter(isSelected),
    [allGroups, isSelected],
  );

  useEffect(() => {
    if (openGroups !== null || !all) return;
    setOpenGroups(new Set(candidates.length <= EXPAND_ALL_UP_TO || allGroups.length === 1 ? allGroups.map((g) => g.key) : []));
  }, [all, candidates.length, allGroups, openGroups]);

  const layout = LABEL_LAYOUTS[opts.layout];
  const isRoll = layout.kind === 'roll';
  const pages = useMemo(() => paginateLabels(items, { layout, copies, skip }), [items, layout, copies, skip]);
  const labelCount = pages.reduce((n, p) => n + p.filter(Boolean).length, 0);
  const located = useMemo(() => locateItems(pages), [pages]);
  const current = Math.min(pageIndex, Math.max(0, pages.length - 1));

  useEffect(() => { if (pageIndex !== current) setPageIndex(current); }, [pageIndex, current]);

  // Obrazki liczone raz na wartość, dla wszystkich kandydatów — zaznaczanie i odznaczanie
  // sztuk nie generuje kodów od nowa (przy kilkuset sztukach to było odczuwalne).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const qr = new Map();
      const bar = new Map();
      for (const item of candidates) {
        const code = labelCodeFor(item);
        if (opts.format !== 'barcode' && !qr.has(code)) qr.set(code, await qrDataUrl(code));
        const barValue = barcodeValueFor(item);
        if (opts.format !== 'qr' && !bar.has(barValue)) {
          try { bar.set(barValue, code128DataUrl(barValue)); } catch (_) { /* wartość spoza Code 128 — zostaje sam tekst */ }
        }
      }
      if (!cancelled) setImages({ qr, bar });
    })();
    return () => { cancelled = true; };
  }, [candidates, opts.format]);

  const hasPages = pages.length > 0;
  // Arkusz skalowany do szerokości maty (i do ~70% wysokości okna, żeby A4 nie przewijało
  // strony). Rolkę, czyli jedną małą etykietę, wolno powiększyć, żeby dało się ją obejrzeć.
  useLayoutEffect(() => {
    const el = matRef.current;
    if (!el) return undefined;
    const measure = () => setMatWidth(el.clientWidth - 32);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [hasPages]);
  const sheetPxW = layout.page.w * MM_TO_PX;
  const sheetPxH = layout.page.h * MM_TO_PX;
  const maxH = typeof window !== 'undefined' ? window.innerHeight * 0.72 : sheetPxH;
  const scale = Math.max(0.2, Math.min(isRoll ? 3 : 1, (matWidth || sheetPxW) / sheetPxW, maxH / sheetPxH));

  // Miniatura bieżącego arkusza zostaje w zasięgu wzroku przy przewijaniu strzałkami.
  // Przewijamy WYŁĄCZNIE pasek miniatur: `scrollIntoView` przewijał też całą stronę, więc
  // zaraz po wejściu strona sama zjeżdżała do paska, gdy leżał poniżej krawędzi ekranu.
  useEffect(() => {
    const strip = filmRef.current;
    const thumb = strip && strip.querySelectorAll('[data-cy="labels-thumb"]')[current];
    if (!thumb) return;
    const left = thumb.offsetLeft;
    if (left < strip.scrollLeft) strip.scrollLeft = left - 8;
    else if (left + thumb.offsetWidth > strip.scrollLeft + strip.clientWidth) {
      strip.scrollLeft = left + thumb.offsetWidth - strip.clientWidth + 8;
    }
  }, [current]);

  const withoutNumber = items.filter((i) => labelCodeFor(i) === systemCodeFor(i.id));
  const nonAsciiForBarcode = opts.format !== 'qr' ? items.filter((i) => !isCode128Safe(labelCodeFor(i))) : [];
  const setOpt = (patch) => setOpts((prev) => ({ ...prev, ...patch }));
  const imagesReady = items.every((i) => (opts.format === 'barcode' || images.qr.has(labelCodeFor(i))));
  const canPrint = labelCount > 0 && imagesReady;

  // Drukuje dokument ramki. Czeka na zdekodowanie obrazków kodów, żeby podgląd druku nie
  // złapał pustych pól (przy kilkuset etykietach dekodowanie trwa chwilę).
  const printLabels = useCallback(() => printFrame(printFrameRef.current), []);

  // Ctrl+P / Cmd+P na tej stronie drukuje etykiety, a nie cały panel.
  useEffect(() => {
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey || String(e.key).toLowerCase() !== 'p' || !canPrint) return;
      e.preventDefault();
      printLabels();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [canPrint, printLabels]);

  const setMany = (list, on) => setRemoved((prev) => {
    const next = new Set(prev);
    list.forEach((i) => (on ? next.delete(Number(i.id)) : next.add(Number(i.id))));
    return next;
  });
  const visibleItems = visibleGroups.flatMap((g) => g.items);
  const toggleOpen = (key) => setOpenGroups((prev) => {
    const next = new Set(prev || []);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const isOpen = (key) => Boolean(query.trim()) || (openGroups ? openGroups.has(key) : false);

  const showOnSheet = (item) => {
    const at = located.get(Number(item.id));
    if (!at) return;
    setPageIndex(at.page);
    setHighlight({ ...at, stamp: Date.now() });
    const mat = matRef.current;
    if (mat && window.matchMedia && window.matchMedia('(max-width: 960px)').matches) {
      mat.scrollIntoView({ block: 'center', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }
  };

  const groupName = (key) => key || t('labels.noCategory');
  const selectedCount = candidates.filter(isSelected).length;
  const summary = (isRoll ? t('labels.summaryRoll') : t('labels.summary'))
    .replace('{labels}', String(labelCount)).replace('{pages}', String(pages.length));
  const bulkScope = query.trim() ? visibleItems : candidates;

  return (
    <PanelContainer style={{ overflow: 'visible' }}>
      <PageHeader>
        <TitleRow>
          <IconButton type="button" onClick={() => navigate(-1)} aria-label={t('common.back')}>
            <ArrowLeftIcon />
          </IconButton>
          <div>
            <Title>{t('labels.title')}</Title>
            <Subtitle>{t('labels.subtitle')}</Subtitle>
          </div>
        </TitleRow>
        <HeaderActions>
          <PrimaryButton
            type="button"
            onClick={printLabels}
            disabled={!canPrint}
            data-cy="labels-print"
          >
            <PrinterIcon size={16} /> {t('labels.printCount').replace('{n}', String(labelCount))}
          </PrimaryButton>
        </HeaderActions>
      </PageHeader>

      {error && <Banner tone="danger">{error}</Banner>}

      <PanelCard style={{ marginBottom: 16 }}>
        <Options>
          <div>
            <FieldLabel htmlFor="labels-layout">{t('labels.layout')}</FieldLabel>
            <SelectDropdown
              id="labels-layout"
              value={opts.layout}
              onChange={(v) => { setOpt({ layout: v }); setSkip(0); setPageIndex(0); }}
              options={Object.keys(LABEL_LAYOUTS).map((k) => ({ value: k, label: t(`labels.layouts.${k}`) }))}
              dataCy="labels-layout"
            />
          </div>

          <div>
            <FieldLabel as="span">{t('labels.format')}</FieldLabel>
            <Radios role="radiogroup" aria-label={t('labels.format')}>
              {FORMATS.map((f) => (
                <label key={f} data-on={opts.format === f}>
                  <input type="radio" name="labels-format" value={f} checked={opts.format === f} onChange={() => setOpt({ format: f })} data-cy={`labels-format-${f}`} />
                  {t(`labels.formats.${f}`)}
                </label>
              ))}
            </Radios>
            <FieldHint>{t('labels.formatHint')}</FieldHint>
          </div>

          <div>
            <FieldLabel htmlFor="labels-copies">{t('labels.copies')}</FieldLabel>
            <TextInput
              id="labels-copies"
              type="number"
              min={1}
              max={MAX_COPIES}
              value={copies}
              onChange={(e) => setCopies(Math.min(MAX_COPIES, Math.max(1, Number(e.target.value) || 1)))}
              data-cy="labels-copies"
            />
          </div>

          {!isRoll && (
            <div>
              <FieldLabel htmlFor="labels-skip">{t('labels.skip')}</FieldLabel>
              <TextInput
                id="labels-skip"
                type="number"
                min={0}
                max={perPage(layout) - 1}
                value={skip}
                onChange={(e) => setSkip(Math.min(perPage(layout) - 1, Math.max(0, Number(e.target.value) || 0)))}
                data-cy="labels-skip"
              />
              <FieldHint>{t('labels.skipHint')}</FieldHint>
            </div>
          )}

          <LabelLogoPicker
            logo={logo}
            loaded={logoSettings.loaded}
            isAdmin={isAdmin}
            showLogo={opts.showLogo}
            onShowLogoChange={(v) => setOpt({ showLogo: v })}
            onSaved={(labelLogo) => setLogoSettings((prev) => ({ ...prev, label_logo: labelLogo }))}
          />
        </Options>
      </PanelCard>

      {all && candidates.length === 0 && !error && (
        <Banner tone="info" data-cy="labels-empty">{t('labels.empty')}</Banner>
      )}

      {withoutNumber.length > 0 && (
        <Banner tone="info" data-cy="labels-system-code-note">
          {t('labels.systemCodeNote').replace('{count}', String(withoutNumber.length))}
        </Banner>
      )}
      {nonAsciiForBarcode.length > 0 && (
        <Banner tone="warning">
          {t('labels.nonAsciiNote').replace('{names}', shortList(nonAsciiForBarcode.map((i) => i.name), {
            more: (n) => t('labels.andMore').replace('{n}', String(n)),
          }))}
        </Banner>
      )}

      {candidates.length > 0 && (
        <Workbench>
          <Picker as="section" aria-labelledby="labels-picker-title">
            <PickerHead>
              <h2 id="labels-picker-title">{t('labels.selection.title')}</h2>
              <span data-cy="labels-selected-count">
                {t('labels.selection.count').replace('{n}', String(selectedCount)).replace('{total}', String(candidates.length))}
              </span>
            </PickerHead>

            {candidates.length > 1 && (
              <>
                <Search>
                  <SearchIcon size={16} />
                  <TextInput
                    type="search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={t('labels.selection.searchShort')}
                    aria-label={t('labels.selection.search')}
                    data-cy="labels-search"
                  />
                </Search>
                <BulkRow>
                  <LinkButton onClick={() => setMany(bulkScope, true)} data-cy="labels-select-all">
                    {query.trim() ? t('labels.selection.selectShown') : t('labels.selection.selectAll')}
                  </LinkButton>
                  <LinkButton onClick={() => setMany(bulkScope, false)} data-cy="labels-select-none">
                    {query.trim() ? t('labels.selection.clearShown') : t('labels.selection.clearAll')}
                  </LinkButton>
                </BulkRow>
              </>
            )}

            <GroupList data-cy="labels-items">
              {visibleGroups.length === 0 && (
                <Muted>{t('labels.selection.noMatches').replace('{q}', query.trim())}</Muted>
              )}
              {visibleGroups.map((g) => {
                const state = groupState(g, isSelected);
                const open = isOpen(g.key);
                const onCount = g.items.filter(isSelected).length;
                const listId = `labels-group-${g.key || '-'}`.replace(/[^\w-]/g, '_');
                return (
                  <div key={g.key || '-'} data-cy="labels-group" data-group={g.key}>
                    <GroupHead>
                      <TriStateBox
                        state={state}
                        onChange={() => setMany(g.items, state !== 'all')}
                        label={t('labels.selection.groupToggle').replace('{name}', groupName(g.key))}
                        data-cy="labels-group-toggle"
                      />
                      <GroupToggle
                        type="button"
                        $open={open}
                        onClick={() => toggleOpen(g.key)}
                        aria-expanded={open}
                        aria-controls={listId}
                        disabled={Boolean(query.trim())}
                        data-cy="labels-group-expand"
                      >
                        <ArrowLeftIcon size={14} />
                        <span className="name">{groupName(g.key)}</span>
                        <span className="count">{onCount}/{g.items.length}</span>
                      </GroupToggle>
                    </GroupHead>
                    {open && (
                      <Rows id={listId}>
                        {g.items.map((i) => {
                          const on = isSelected(i);
                          return (
                            <Row key={i.id} $on={on} data-cy="labels-item" data-id={i.id}>
                              <input
                                type="checkbox"
                                checked={on}
                                onChange={() => setMany([i], !on)}
                                aria-label={i.name}
                                data-cy="labels-item-toggle"
                              />
                              <RowName
                                type="button"
                                onClick={() => showOnSheet(i)}
                                disabled={!on}
                                title={on ? t('labels.selection.showOnSheet') : undefined}
                              >
                                {i.name}
                              </RowName>
                              <code>{labelCodeFor(i)}</code>
                            </Row>
                          );
                        })}
                      </Rows>
                    )}
                  </div>
                );
              })}
            </GroupList>
          </Picker>

          <Stage as="section" aria-label={t('labels.preview')} data-cy="labels-preview">
            <StageBar>
              <strong style={{ fontSize: 'var(--fs-sm)' }} data-cy="labels-summary">{summary}</strong>
              {pages.length > 1 && (
                <PageNav>
                  <IconButton
                    type="button"
                    onClick={() => setPageIndex(Math.max(0, current - 1))}
                    disabled={current === 0}
                    aria-label={isRoll ? t('labels.prevLabel') : t('labels.prevSheet')}
                    data-cy="labels-page-prev"
                  >
                    <ArrowLeftIcon size={16} />
                  </IconButton>
                  <output aria-live="polite" data-cy="labels-page-indicator">
                    {(isRoll ? t('labels.labelOf') : t('labels.sheetOf'))
                      .replace('{n}', String(current + 1)).replace('{total}', String(pages.length))}
                  </output>
                  <IconButton
                    type="button"
                    onClick={() => setPageIndex(Math.min(pages.length - 1, current + 1))}
                    disabled={current >= pages.length - 1}
                    aria-label={isRoll ? t('labels.nextLabel') : t('labels.nextSheet')}
                    data-cy="labels-page-next"
                  >
                    <ArrowLeftIcon size={16} style={{ transform: 'rotate(180deg)' }} />
                  </IconButton>
                </PageNav>
              )}
            </StageBar>

            {!isRoll && pages.length > 1 && (
              <>
                <Filmstrip ref={filmRef} aria-label={t('labels.sheets')} data-cy="labels-filmstrip">
                  {pages.map((p, i) => (
                    <PageThumb
                      key={i}
                      page={p}
                      index={i}
                      layout={layout}
                      skip={skip}
                      current={i === current}
                      onSelect={setPageIndex}
                      t={t}
                    />
                  ))}
                </Filmstrip>
                <Legend aria-hidden="true">
                  <li><Swatch $state="item" />{t('labels.legend.label')}</li>
                  {skip > 0 && <li><Swatch $state="skip" />{t('labels.legend.skipped')}</li>}
                  <li><Swatch $state="empty" />{t('labels.legend.free')}</li>
                </Legend>
              </>
            )}
            {isRoll && pages.length > 1 && (
              <Range
                type="range"
                min={1}
                max={pages.length}
                value={current + 1}
                onChange={(e) => setPageIndex(Number(e.target.value) - 1)}
                aria-label={t('labels.labelOf').replace('{n}', String(current + 1)).replace('{total}', String(pages.length))}
                data-cy="labels-roll-range"
              />
            )}

            <Mat ref={matRef}>
              {pages.length > 0 ? (
                <Viewport style={{ width: sheetPxW * scale, height: sheetPxH * scale }}>
                  <Scaled style={{ transform: `scale(${scale})` }}>
                    <Sheets
                      pages={[pages[current]]}
                      firstIndex={current}
                      layout={layout}
                      format={opts.format}
                      logo={printedLogo}
                      images={images}
                      dataCy="labels"
                      highlight={highlight}
                    />
                  </Scaled>
                </Viewport>
              ) : (
                <Muted data-cy="labels-nothing-selected">{t('labels.selection.nothingSelected')}</Muted>
              )}
            </Mat>
          </Stage>
        </Workbench>
      )}

      <PrintFrame
        frameRef={printFrameRef}
        title={t('labels.title')}
        page={`size: ${layout.page.w}mm ${layout.page.h}mm; margin: 0;`}
        rootId="equipment-labels-print"
        dataCy="labels-print-frame"
      >
        <Sheets pages={pages} layout={layout} format={opts.format} logo={printedLogo} images={images} />
      </PrintFrame>
    </PanelContainer>
  );
}
